/**
 * Media_Service.ts
 * Field-scoped async media upload pipeline.
 * (sk-catalog-api /api/v1/items/:id/fields/:fieldKey/media)
 *
 * Flow:
 *   1. initiateUpload()  → POST .../media           → { upload, presigned PUT url }
 *   2. PUT the bytes to the presigned url (done by the caller / browser)
 *   3. completeUpload()  → POST .../media/complete  → PROCESSING → READY
 *   On failure, clearUpload() resets the field so a retry can start clean.
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Media_Url,
  Catalog_Media_Complete_Url,
  Catalog_Media_Clear_Url,
} from '../../api/endpoints';
import {
  MediaInitiatePayload,
  MediaInitiateResponse,
  MediaCompletePayload,
  MediaResponse,
} from '../../schemas/catalog/media';

/* ─────────────────────────────────────────
   Initiate — POST /api/v1/items/:id/fields/:fieldKey/media
   Creates a PENDING media record and returns a presigned PUT URL.
───────────────────────────────────────── */

export const initiateUpload = async (
  itemId: string,
  fieldKey: string,
  payload: MediaInitiatePayload,
): Promise<MediaInitiateResponse> => {
  const { data } = await axiosInstance.post<MediaInitiateResponse>(
    Catalog_Media_Url(itemId, fieldKey),
    payload,
  );
  return data;
};

/* ─────────────────────────────────────────
   Complete — POST /api/v1/items/:id/fields/:fieldKey/media/complete
   Finalizes the upload and triggers PROCESSING → READY.
───────────────────────────────────────── */

export const completeUpload = async (
  itemId: string,
  fieldKey: string,
  payload?: MediaCompletePayload,
): Promise<MediaResponse> => {
  const { data } = await axiosInstance.post<MediaResponse>(
    Catalog_Media_Complete_Url(itemId, fieldKey),
    payload ?? {},
  );
  return data;
};

/* ─────────────────────────────────────────
   Status — GET /api/v1/items/:id/fields/:fieldKey/media
   Returns current media status + CDN URL.
───────────────────────────────────────── */

export const getMediaStatus = async (itemId: string, fieldKey: string): Promise<MediaResponse> => {
  const { data } = await axiosInstance.get<MediaResponse>(Catalog_Media_Url(itemId, fieldKey));
  return data;
};

/* ─────────────────────────────────────────
   Clear — POST /api/v1/items/:id/fields/:fieldKey/media/clear
   Clears a failed/abandoned upload so the field can be retried.
───────────────────────────────────────── */

export const clearUpload = async (itemId: string, fieldKey: string): Promise<MediaResponse> => {
  const { data } = await axiosInstance.post<MediaResponse>(Catalog_Media_Clear_Url(itemId, fieldKey));
  return data;
};
